import { useState, useEffect, useRef } from 'react'
import {
  Typography,
  Stack,
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Table,
  Sheet,
  Chip,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Option,
  CircularProgress,
  Tooltip,
  Switch,
} from '@mui/joy'
import {
  Speed as QuickRepliesIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoFile as VideoFileIcon,
  AudioFile as AudioFileIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material'
import api from '../services/api'

interface QuickMessage {
  id: number
  shortcode: string
  message: string
  geral?: boolean
  mediaPath?: string
  mediaName?: string
  userId?: number
  createdAt: string
  /** Descripción semántica para búsqueda por IA */
  intent?: string
  /** Key estable para matching por IA */
  intentKey?: string
  /** Si está habilitado para uso por el orquestador IA */
  isAiEnabled?: boolean
}

// Helper para detectar si un archivo/URL es imagen por extensión
const isImageFile = (name: string): boolean => /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)
const isVideoFile = (name: string): boolean => /\.(mp4|mov|avi|webm|mkv)$/i.test(name)
const isAudioFile = (name: string): boolean => /\.(mp3|wav|ogg|opus|aac|m4a)$/i.test(name)

const getFileIcon = (name: string) => {
  if (isImageFile(name)) return <ImageIcon />
  if (isVideoFile(name)) return <VideoFileIcon />
  if (isAudioFile(name)) return <AudioFileIcon />
  return <FileIcon />
}

// Funcionalidad de IA semántica ("Habilitar para IA" / intent / key) temporalmente
// desactivada. Cambiar a true para reactivar el toggle del modal, la tarjeta
// "Habilitados para IA" y la columna "IA" de la tabla.
const AI_FEATURES_ENABLED = false
const PAGE_SIZE = 20

interface QuickMessageStats {
  total: number
  global: number
  personal: number
  withMedia: number
  aiEnabled: number
}

export default function QuickReplies() {
  const [messages, setMessages] = useState<QuickMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [stats, setStats] = useState<QuickMessageStats>({
    total: 0,
    global: 0,
    personal: 0,
    withMedia: 0,
    aiEnabled: 0,
  })
  const [openModal, setOpenModal] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<QuickMessage | null>(null)
  const [formData, setFormData] = useState({
    shortcode: '',
    message: '',
    geral: true,
    intentKey: '',
    intent: '',
    isAiEnabled: false,
  })

  // Media upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [existingMedia, setExistingMedia] = useState<{ path: string; name: string } | null>(null)
  const [removeMedia, setRemoveMedia] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [suggestingIntent, setSuggestingIntent] = useState(false)
  const [redrafting, setRedrafting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchMessages()
    }, 250)

    return () => window.clearTimeout(timer)
  }, [pageNumber, searchTerm, categoryFilter])

  const fetchMessages = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/quick-messages', {
        params: {
          pageNumber,
          searchParam: searchTerm,
          categoryFilter,
        },
      })

      setMessages(Array.isArray(data) ? data : (data.records || []))
      setTotalCount(Array.isArray(data) ? data.length : Number(data.count || 0))
      setStats(data.stats || {
        total: Number(data.count || 0),
        global: 0,
        personal: 0,
        withMedia: 0,
        aiEnabled: 0,
      })
    } catch (error) {
      console.error('Error fetching quick messages:', error)
      setMessages([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }

  const uploadMedia = async (quickMessageId: number) => {
    if (!selectedFile) return
    const fd = new FormData()
    fd.append('file', selectedFile)
    fd.append('typeArch', 'quickMessage')
    // Axios maneja automáticamente el Content-Type: multipart/form-data con boundary cuando usas FormData
    await api.post(`/quick-messages/${quickMessageId}/media-upload`, fd)
  }

  const deleteMediaFromServer = async (quickMessageId: number) => {
    await api.delete(`/quick-messages/${quickMessageId}/media-upload`)
  }

  const fileToDataUrl = (file: File): Promise<string> => (
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  )

  const requestAiIntentSuggestion = async () => {
    let mediaDataUrl: string | undefined

    if (selectedFile?.type.startsWith('image/') && selectedFile.size <= 4 * 1024 * 1024) {
      mediaDataUrl = await fileToDataUrl(selectedFile)
    }

    const response = await api.post('/quick-messages/ai/suggest-intent', {
      shortcode: formData.shortcode,
      message: formData.message,
      mediaName: selectedFile?.name || existingMedia?.name,
      mediaUrl: !selectedFile ? existingMedia?.path : undefined,
      mediaDataUrl,
    })

    return response.data || {}
  }

  const ensureAiIntentBeforeSave = async () => {
    if (!formData.isAiEnabled || (formData.intent.trim() && formData.intentKey.trim())) {
      return formData
    }

    try {
      const suggestion = await requestAiIntentSuggestion()
      const nextFormData = {
        ...formData,
        intentKey: formData.intentKey || suggestion.intentKey || '',
        intent: formData.intent || suggestion.intent || '',
      }
      setFormData(nextFormData)
      return nextFormData
    } catch (error) {
      console.error('Error ensuring AI intent:', error)
      return formData
    }
  }

  const handleSuggestAiIntent = async () => {
    try {
      setSuggestingIntent(true)
      setFormData((prev) => ({
        ...prev,
        isAiEnabled: true,
        intentKey: '',
        intent: '',
      }))
      const suggestion = await requestAiIntentSuggestion()

      setFormData((prev) => ({
        ...prev,
        isAiEnabled: true,
        intentKey: suggestion.intentKey || '',
        intent: suggestion.intent || '',
      }))
    } catch (error) {
      console.error('Error generating AI intent:', error)
    } finally {
      setSuggestingIntent(false)
    }
  }

  const handleRedraftWithAI = async () => {
    if (!formData.message.trim()) return
    try {
      setRedrafting(true)
      const response = await api.post('/quick-messages/ai/redraft', {
        shortcode: formData.shortcode,
        message: formData.message,
      })
      const redrafted = response.data?.message
      if (redrafted && typeof redrafted === 'string') {
        setFormData((prev) => ({ ...prev, message: redrafted }))
      }
    } catch (error) {
      console.error('Error redactando con IA:', error)
    } finally {
      setRedrafting(false)
    }
  }

  const handleCreate = async () => {
    try {
      setUploading(true)
      const payload = await ensureAiIntentBeforeSave()
      const response = await api.post('/quick-messages', payload)
      const newId = response.data.id
      if (selectedFile && newId) {
        await uploadMedia(newId)
      }
      fetchMessages()
      setOpenModal(false)
      resetForm()
    } catch (error) {
      console.error('Error creating quick message:', error)
    } finally {
      setUploading(false)
    }
  }

  const handleUpdate = async () => {
    if (!selectedMessage) return
    try {
      setUploading(true)
      // Si se marcó para eliminar media (y no hay archivo nuevo)
      if (removeMedia && !selectedFile && existingMedia) {
        await deleteMediaFromServer(selectedMessage.id)
      }
      const payload = await ensureAiIntentBeforeSave()
      await api.put(`/quick-messages/${selectedMessage.id}`, payload)
      // Si hay nuevo archivo, subir (si había media anterior, el backend la reemplaza)
      if (selectedFile) {
        if (existingMedia) {
          await deleteMediaFromServer(selectedMessage.id)
        }
        await uploadMedia(selectedMessage.id)
      }
      fetchMessages()
      setOpenModal(false)
      resetForm()
    } catch (error) {
      console.error('Error updating quick message:', error)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (messageId: number) => {
    if (confirm('¿Estás seguro de eliminar este mensaje rápido?')) {
      try {
        await api.delete(`/quick-messages/${messageId}`)
        fetchMessages()
      } catch (error) {
        console.error('Error deleting quick message:', error)
      }
    }
  }

  const handleCopyMessage = (message: string) => {
    navigator.clipboard.writeText(message)
    // Could add a toast notification here
  }

  const openEditModal = (message: QuickMessage) => {
    setSelectedMessage(message)
    setFormData({
      shortcode: message.shortcode,
      message: message.message,
      geral: message.geral ?? true,
      intentKey: message.intentKey || '',
      intent: message.intent || '',
      isAiEnabled: message.isAiEnabled ?? false,
    })
    setExistingMedia(
      message.mediaPath
        ? { path: message.mediaPath, name: message.mediaName || 'archivo' }
        : null
    )
    setSelectedFile(null)
    setRemoveMedia(false)
    setOpenModal(true)
  }

  const openCreateModal = () => {
    setSelectedMessage(null)
    resetForm()
    setOpenModal(true)
  }

  const resetForm = () => {
    setFormData({
      shortcode: '',
      message: '',
      geral: true,
      intentKey: '',
      intent: '',
      isAiEnabled: false,
    })
    setSelectedFile(null)
    setExistingMedia(null)
    setRemoveMedia(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setRemoveMedia(false)
    }
    // Reset input para permitir re-seleccionar el mismo archivo
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    if (existingMedia) {
      setRemoveMedia(true)
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const firstVisible = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1
  const lastVisible = Math.min(pageNumber * PAGE_SIZE, totalCount)

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <QuickRepliesIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography level="h2">Mensajes Rápidos</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Respuestas predefinidas para agilizar las conversaciones
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton variant="outlined" color="neutral" onClick={fetchMessages}>
              <RefreshIcon />
            </IconButton>
            <Button startDecorator={<AddIcon />} color="primary" onClick={openCreateModal}>
              Nuevo Mensaje
            </Button>
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Total Mensajes
                </Typography>
                <Typography level="h2">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Globales
                </Typography>
                <Typography level="h2" sx={{ color: 'success.main' }}>
                  {stats.global}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Personales
                </Typography>
                <Typography level="h2" sx={{ color: 'primary.main' }}>
                  {stats.personal}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Con Archivos
                </Typography>
                <Typography level="h2">{stats.withMedia}</Typography>
              </CardContent>
            </Card>
          </Grid>
          {AI_FEATURES_ENABLED && (
          <Grid xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.softBg' }}>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Habilitados para IA
                </Typography>
                <Typography level="h2" sx={{ color: 'primary.500' }}>
                  {stats.aiEnabled}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          )}
        </Grid>

        {/* Filters */}
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Input
                placeholder="Buscar mensajes rápidos..."
                startDecorator={<SearchIcon />}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPageNumber(1)
                }}
                sx={{ flexGrow: 1 }}
              />
              <Select
                value={categoryFilter}
                onChange={(_, value) => {
                  setCategoryFilter(value as string)
                  setPageNumber(1)
                }}
                sx={{ minWidth: 180 }}
              >
                <Option value="all">Todos</Option>
                <Option value="global">Globales</Option>
                <Option value="personal">Personales</Option>
              </Select>
            </Stack>
          </CardContent>
        </Card>

        {/* Quick Messages Table */}
        <Card>
          <Sheet sx={{ overflow: 'auto' }}>
            <Table stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Atajo</th>
                  <th>Mensaje</th>
                  <th style={{ width: 100 }}>Tipo</th>
                  <th style={{ width: 100 }}>Archivo</th>
                  {AI_FEATURES_ENABLED && <th style={{ width: 60 }}>IA</th>}
                  <th style={{ width: 180 }}>Fecha Creación</th>
                  <th style={{ width: 180 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={AI_FEATURES_ENABLED ? 7 : 6} style={{ textAlign: 'center', padding: '2rem' }}>
                      <Typography>Cargando mensajes...</Typography>
                    </td>
                  </tr>
                ) : messages.length === 0 ? (
                  <tr>
                    <td colSpan={AI_FEATURES_ENABLED ? 7 : 6} style={{ textAlign: 'center', padding: '2rem' }}>
                      <Typography>No se encontraron mensajes</Typography>
                    </td>
                  </tr>
                ) : (
                  messages.map((message) => (
                    <tr key={message.id}>
                      <td>
                        <Chip size="sm" variant="soft" color="primary">
                          {message.shortcode}
                        </Chip>
                      </td>
                      <td>
                        <Typography level="body-sm" noWrap sx={{ maxWidth: 400 }}>
                          {message.message}
                        </Typography>
                      </td>
                      <td>
                        <Chip size="sm" color={message.geral ? 'success' : 'neutral'}>
                          {message.geral ? 'Global' : 'Personal'}
                        </Chip>
                      </td>
                      <td>
                        {message.mediaPath ? (
                          <Tooltip title={message.mediaName || 'Ver archivo'}>
                            <IconButton
                              size="sm"
                              variant="soft"
                              color="primary"
                              onClick={() => window.open(message.mediaPath, '_blank')}
                            >
                              {getFileIcon(message.mediaName || '')}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            -
                          </Typography>
                        )}
                      </td>
                      {AI_FEATURES_ENABLED && (
                      <td>
                        {message.isAiEnabled ? (
                          <Tooltip title={`Key: ${message.intentKey || '(sin key)'} | Intent: ${message.intent || '(sin descripción)'}`}>
                            <Chip
                              size="sm"
                              color="primary"
                              variant="soft"
                              startDecorator={<AutoAwesomeIcon sx={{ fontSize: 12 }} />}
                            >
                              IA
                            </Chip>
                          </Tooltip>
                        ) : (
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            -
                          </Typography>
                        )}
                      </td>
                      )}
                      <td>
                        <Typography level="body-xs">
                          {new Date(message.createdAt).toLocaleDateString('es-ES')}
                        </Typography>
                      </td>
                      <td>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => handleCopyMessage(message.message)}
                            title="Copiar mensaje"
                          >
                            <CopyIcon />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="primary"
                            onClick={() => openEditModal(message)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(message.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Sheet>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              Mostrando {firstVisible}-{lastVisible} de {totalCount}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                disabled={pageNumber <= 1 || loading}
                onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
              >
                Anterior
              </Button>
              <Typography level="body-sm">
                Página {pageNumber} de {totalPages}
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                disabled={pageNumber >= totalPages || loading}
                onClick={() => setPageNumber((current) => Math.min(totalPages, current + 1))}
              >
                Siguiente
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* Modal Create/Edit */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <ModalDialog
            sx={{
              width: { xs: 'calc(100vw - 24px)', sm: 720, md: 900 },
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - 32px)',
              p: 0,
              overflow: 'hidden',
            }}
          >
            <ModalClose />
            <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
              <Typography level="h4">
                {selectedMessage ? 'Editar Mensaje Rápido' : 'Nuevo Mensaje Rápido'}
              </Typography>
            </Box>
            <Stack
              spacing={2}
              sx={{
                px: 3,
                pb: 2,
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 150px)',
              }}
            >
              <FormControl>
                <FormLabel>Atajo</FormLabel>
                <Input
                  value={formData.shortcode}
                  onChange={(e) => setFormData({ ...formData, shortcode: e.target.value })}
                  placeholder="/ejemplo"
                  startDecorator="/"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Mensaje</FormLabel>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Escribe el mensaje predefinido..."
                  minRows={4}
                  maxRows={8}
                />
                <Button
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={redrafting ? <CircularProgress size="sm" /> : <AutoAwesomeIcon />}
                  onClick={handleRedraftWithAI}
                  disabled={redrafting || !formData.message.trim()}
                  sx={{ alignSelf: 'flex-start', mt: 1 }}
                >
                  {redrafting ? 'Redactando...' : 'Redactar con IA'}
                </Button>
              </FormControl>
              <FormControl>
                <FormLabel>Tipo de Mensaje</FormLabel>
                <Select
                  value={formData.geral ? 'global' : 'personal'}
                  onChange={(_, value) => setFormData({ ...formData, geral: value === 'global' })}
                >
                  <Option value="global">Global (Todos los usuarios)</Option>
                  <Option value="personal">Personal (Solo yo)</Option>
                </Select>
              </FormControl>

              {/* Sección IA (desactivada temporalmente — AI_FEATURES_ENABLED) */}
              {AI_FEATURES_ENABLED && (
              <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm', border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-sm" fontWeight="bold">
                      <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.500' }} />
                      Habilitar para IA
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                      Permite que el orquestador IA use este mensaje en respuestas semánticas
                    </Typography>
                  </Box>
                  <Switch
                    checked={formData.isAiEnabled}
                    onChange={(e) => setFormData({ ...formData, isAiEnabled: e.target.checked })}
                    color={formData.isAiEnabled ? 'primary' : 'neutral'}
                  />
                </Stack>

                {/* Campo de intención — solo visible si IA está habilitada */}
                {formData.isAiEnabled && (
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    <Button
                      size="sm"
                      variant="soft"
                      color="primary"
                      startDecorator={suggestingIntent ? <CircularProgress size="sm" /> : <AutoAwesomeIcon />}
                      onClick={handleSuggestAiIntent}
                      disabled={suggestingIntent || (!formData.message.trim() && !selectedFile && !existingMedia)}
                    >
                      {suggestingIntent ? 'Generando...' : 'Generar key con IA'}
                    </Button>

                    <FormControl>
                      <FormLabel sx={{ fontSize: 'sm' }}>
                        Key IA
                      </FormLabel>
                      <Input
                        value={formData.intentKey}
                        onChange={(e) => setFormData({ ...formData, intentKey: e.target.value })}
                        placeholder="location_question, plan_gold_selection, pricing_question"
                        size="sm"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel sx={{ fontSize: 'sm' }}>
                        Intención (para búsqueda semántica)
                      </FormLabel>
                      <Input
                        value={formData.intent}
                        onChange={(e) => setFormData({ ...formData, intent: e.target.value })}
                        placeholder="Ej: usar cuando el cliente pregunte ubicación, dirección o mapa"
                        size="sm"
                      />
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                        La búsqueda IA usa la key, el mensaje, el atajo y el archivo para encontrar esta respuesta.
                      </Typography>
                    </FormControl>
                  </Stack>
                )}
              </Box>
              )}

              {/* Archivo multimedia */}
              <FormControl>
                <FormLabel>Archivo Multimedia (opcional)</FormLabel>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                  style={{ display: 'none' }}
                />

                {/* Estado: sin archivo */}
                {!selectedFile && !existingMedia?.path && !removeMedia && (
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: '2px dashed',
                      borderColor: 'neutral.300',
                      borderRadius: 'sm',
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                      '&:hover': { borderColor: 'primary.400' },
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 36, color: 'neutral.400', mb: 1 }} />
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Haz clic para adjuntar imagen, video, audio o documento
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                      Formatos: JPG, PNG, GIF, MP4, MP3, PDF, DOC, XLS...
                    </Typography>
                  </Box>
                )}

                {/* Estado: archivo nuevo seleccionado */}
                {selectedFile && (
                  <Box sx={{ border: '1px solid', borderColor: 'primary.300', borderRadius: 'sm', p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {selectedFile.type.startsWith('image/') ? (
                        <Box
                          component="img"
                          src={URL.createObjectURL(selectedFile)}
                          alt="preview"
                          sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 'sm' }}
                        />
                      ) : (
                        <Box sx={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.softBg', borderRadius: 'sm' }}>
                          {getFileIcon(selectedFile.name)}
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="body-sm" fontWeight="bold" noWrap>
                          {selectedFile.name}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Cambiar archivo">
                          <IconButton size="sm" variant="plain" color="primary" onClick={() => fileInputRef.current?.click()}>
                            <CloudUploadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Quitar archivo">
                          <IconButton size="sm" variant="plain" color="danger" onClick={handleRemoveFile}>
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                )}

                {/* Estado: archivo existente (editando) */}
                {!selectedFile && existingMedia?.path && !removeMedia && (
                  <Box sx={{ border: '1px solid', borderColor: 'success.300', borderRadius: 'sm', p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {isImageFile(existingMedia.name) ? (
                        <Box
                          component="img"
                          src={existingMedia.path}
                          alt="media actual"
                          sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 'sm' }}
                        />
                      ) : (
                        <Box sx={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'success.softBg', borderRadius: 'sm' }}>
                          {getFileIcon(existingMedia.name)}
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="body-sm" fontWeight="bold" noWrap>
                          {existingMedia.name}
                        </Typography>
                        <Chip size="sm" color="success" variant="soft" sx={{ mt: 0.5 }}>
                          Archivo actual
                        </Chip>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Reemplazar archivo">
                          <IconButton size="sm" variant="plain" color="primary" onClick={() => fileInputRef.current?.click()}>
                            <CloudUploadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar archivo">
                          <IconButton size="sm" variant="plain" color="danger" onClick={handleRemoveFile}>
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                )}

                {/* Estado: archivo eliminado (se marcó para borrar) */}
                {!selectedFile && removeMedia && (
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: '2px dashed',
                      borderColor: 'danger.300',
                      borderRadius: 'sm',
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.400' },
                    }}
                  >
                    <Typography level="body-sm" sx={{ color: 'danger.500' }}>
                      Archivo marcado para eliminar
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                      Haz clic para adjuntar uno nuevo, o guarda para eliminar
                    </Typography>
                  </Box>
                )}
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                <Typography
                  level="body-sm"
                  sx={{
                    flex: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  <strong>Vista Previa:</strong><br />
                  {formData.shortcode || '/atajo'} → {formData.message || 'Mensaje aquí...'}
                  {(selectedFile || (existingMedia && !removeMedia)) && (
                    <>
                      <br />
                      <AttachFileIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      {selectedFile?.name || existingMedia?.name}
                    </>
                  )}
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.surface',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                color="neutral"
                variant="plain"
                onClick={() => setOpenModal(false)}
                disabled={uploading}
              >
                Cancelar
              </Button>
              <Button
                color="primary"
                onClick={selectedMessage ? handleUpdate : handleCreate}
                disabled={uploading}
                startDecorator={uploading ? <CircularProgress size="sm" /> : undefined}
              >
                {uploading ? 'Guardando...' : `${selectedMessage ? 'Actualizar' : 'Crear'} Mensaje`}
              </Button>
            </Box>
          </ModalDialog>
        </Modal>
      </Stack>
    </Container>
  )
}
